import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle2, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  validatePassword,
  validatePasswordMatch,
  getPasswordStrength,
  getStrengthColor,
  getStrengthTextColor,
} from '../lib/passwordValidation';
import { logger } from '@/lib/logger';
import background from '/media/background.png';

// Polish translations for password strength
const strengthLabels = {
  weak: 'SŁABE',
  fair: 'ŚREDNIE',
  good: 'DOBRE',
  strong: 'SILNE'
};

export default function SetPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Use ref to prevent multiple redirects
  const hasRedirected = useRef(false);

  // Listen for auth state changes and establish session from invite/recovery URL (hash or query)
  useEffect(() => {
    logger.debug('SetPassword: Setting up auth listener');

    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const searchParams = new URLSearchParams(window.location.search);

    // Tokens can be in hash (access_token/refresh_token) or in query (token_hash + type for PKCE)
    const hasHashTokens = hashParams.has('access_token') || hashParams.has('token');
    const tokenHash = searchParams.get('token_hash') || hashParams.get('token_hash');
    const tokenType = searchParams.get('type') || hashParams.get('type');
    const hasAuthTokens = hasHashTokens || (tokenHash && tokenType);
    const errorCode = hashParams.get('error_code') || searchParams.get('error_code');
    const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');

    logger.debug('URL check:', {
      hasHashTokens,
      hasTokenHash: !!tokenHash,
      tokenType,
      errorCode,
      hash: window.location.hash ? 'present' : 'empty',
      search: window.location.search || 'empty'
    });

    if (errorCode || errorDescription) {
      logger.error('Auth error from URL:', { errorCode });
      toast.error(decodeURIComponent(errorDescription || 'Wystąpił błąd podczas autoryzacji. Spróbuj ponownie.'));
      hasRedirected.current = true;
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.debug('SetPassword auth event:', { event, hasSession: !!session });
        if (session?.user && (event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION')) {
          setIsRecoveryMode(true);
          setUserEmail(session.user.email);
          logger.debug('Recovery/invite mode detected');
        }
      }
    );

    const checkExistingSession = async () => {
      // If URL has token_hash + type (e.g. PKCE/invite redirect), we must exchange them for a session
      if (tokenHash && tokenType) {
        logger.debug('Exchanging token_hash for session via verifyOtp');
        const { data: { session }, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: tokenType
        });
        if (error) {
          logger.error('verifyOtp error:', error);
          toast.error(error.message || 'Nieprawidłowy lub wygasły link. Spróbuj ponownie lub poproś o nowe zaproszenie.');
          hasRedirected.current = true;
          setTimeout(() => navigate('/login'), 2000);
          return;
        }
        if (session?.user) {
          setIsRecoveryMode(true);
          setUserEmail(session.user.email);
          // Clean URL so refresh doesn't re-use the one-time token
          window.history.replaceState({}, document.title, window.location.pathname);
          logger.debug('Session established from token_hash');
          return;
        }
      }

      // Give Supabase a moment to process URL hash (access_token/refresh_token) when detectSessionInUrl runs
      await new Promise(resolve => setTimeout(resolve, 500));

      const { data: { session }, error } = await supabase.auth.getSession();
      logger.debug('Existing session check:', { hasSession: !!session, hasError: !!error });

      if (error) {
        logger.error('Error checking session:', error);
        toast.error('Błąd sesji: ' + error.message);
      }

      if (session?.user) {
        setIsRecoveryMode(true);
        setUserEmail(session.user.email);
        logger.debug('Session found');
      } else if (!hasAuthTokens && !hasRedirected.current) {
        hasRedirected.current = true;
        logger.debug('No session and no auth tokens, redirecting to login');
        toast.error('Nieprawidłowy lub wygasły link do resetowania hasła');
        navigate('/login');
      }
    };

    checkExistingSession();

    return () => subscription.unsubscribe();
  }, [navigate]);

  const passwordValidation = validatePassword(password);
  const passwordStrength = getPasswordStrength(password);
  const matchValidation = validatePasswordMatch(password, confirmPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordTouched(true);

    // Validate password
    if (!passwordValidation.isValid) {
      toast.error('Proszę poprawić błędy walidacji hasła');
      return;
    }

    // Validate password match
    if (!matchValidation.isValid) {
      toast.error('Hasła nie są zgodne');
      return;
    }

    setLoading(true);

    try {
      // Get current session to verify user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      logger.debug('Session verified before password update');

      // Update the user's password
      logger.debug('Attempting to update password');
      const { data, error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        logger.error('Password update error:', error);
        // Translate Supabase auth errors to Polish
        const errorTranslations = {
          'New password should be different from the old password': 'Nowe hasło musi być inne niż poprzednie',
          'Password should be at least 6 characters': 'Hasło musi mieć co najmniej 6 znaków',
          'Password is too weak': 'Hasło jest zbyt słabe',
          'Auth session missing!': 'Sesja wygasła lub link jest nieprawidłowy. Kliknij link z zaproszenia ponownie lub poproś o nowe zaproszenie.',
        };
        const translatedMessage = errorTranslations[error.message] || error.message;
        throw new Error(translatedMessage);
      }

      logger.debug('Password updated successfully');
      toast.success('Hasło ustawione pomyślnie! Przekierowywanie...');

      // Get user profile to determine where to redirect
      const { data: profile, error: profileError } = await supabase
        .from('app_users')
        .select('primary_role, metadata')
        .eq('id', data.user.id)
        .single();

      if (profileError) {
        logger.error('Error fetching profile:', profileError);
        // Default redirect to home
        navigate('/');
        return;
      }

      // Update user status from 'pending_password_reset' to 'active'
      if (profile.metadata?.status === 'pending_password_reset' || profile.metadata?.status === 'invited') {
        const { error: updateError } = await supabase
          .from('app_users')
          .update({
            metadata: { ...profile.metadata, status: 'active' }
          })
          .eq('id', data.user.id);

        if (updateError) {
          logger.error('Error updating user status:', updateError);
          // Non-critical, continue with redirect
        } else {
          logger.debug('User status updated to active');
        }
      }

      // Redirect to home - the app handles role-based routing automatically
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      logger.error('Error setting password:', error);
      toast.error(error.message || 'Nie udało się ustawić hasła. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  if (!isRecoveryMode) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative">
        {/* Background image - same as Login */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${background})` }}
        />
        <div className="absolute inset-0 bg-white/60" />
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 relative z-10" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      {/* Background image - same as Login */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${background})` }}
      />
      <div className="absolute inset-0 bg-white/60" />

      <Card className="w-full max-w-md relative z-10 shadow-xl">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <div className="h-16 w-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
              <Lock className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">
            Ustaw swoje hasło
          </CardTitle>
          <p className="text-sm text-gray-600 text-center">
            Witaj w KConnect! Utwórz bezpieczne hasło dla swojego konta.
          </p>
          {userEmail && (
            <p className="text-sm text-gray-500 text-center">{userEmail}</p>
          )}
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password Requirements Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5" />
                Wymagania dotyczące hasła
              </h3>
              <ul className="text-xs text-blue-800 space-y-0.5">
                <li>• Minimum 12 znaków</li>
                <li>• Wielkie i małe litery</li>
                <li>• Co najmniej jedna cyfra</li>
                <li>• Co najmniej jeden znak specjalny (!@#$%^&*)</li>
              </ul>
            </div>

            {/* Password Input */}
            <div>
              <Label htmlFor="password">Nowe hasło</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setPasswordTouched(true)}
                  placeholder="Wprowadź hasło"
                  className="h-11 rounded-xl pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Siła hasła:</span>
                    <span className={`text-xs font-semibold ${getStrengthTextColor(passwordStrength.strength)}`}>
                      {strengthLabels[passwordStrength.strength] || passwordStrength.strength.toUpperCase()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength.strength)}`}
                      style={{ width: `${(passwordStrength.score / 8) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Password Validation Errors */}
              {passwordTouched && password && !passwordValidation.isValid && (
                <div className="mt-2 space-y-0.5">
                  {passwordValidation.errors.map((error, index) => (
                    <div key={index} className="flex items-center gap-1.5 text-xs text-red-600">
                      <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Password Validation Success */}
              {passwordTouched && password && passwordValidation.isValid && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-green-600">
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Hasło spełnia wszystkie wymagania</span>
                </div>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <Label htmlFor="confirmPassword">Potwierdź hasło</Label>
              <div className="relative mt-1">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Wprowadź hasło ponownie"
                  className="h-11 rounded-xl pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>

              {/* Password Match Validation */}
              {confirmPassword && !matchValidation.isValid && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                  <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Hasła nie są zgodne</span>
                </div>
              )}

              {confirmPassword && matchValidation.isValid && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-green-600">
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Hasła są zgodne</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !passwordValidation.isValid || !matchValidation.isValid}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {loading ? 'Ustawianie hasła...' : 'Ustaw hasło i kontynuuj'}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-gray-500 mt-4">
            Ustawiając hasło, akceptujesz Regulamin i Politykę Prywatności KConnect.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
